import { useEffect } from 'react'
import { LockIcon, UsersIcon } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  WorkspaceOverviewFormData,
  WorkspaceOverviewSchema,
} from '@app/modules/Risk/shared/types/riskWorkspace.types'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useAtom, useSetAtom } from 'jotai'
import {
  riskWorkspaceAtom,
  riskWorkspaceStepAtom,
} from '@app/modules/Risk/shared/state/riskWorkspace.atom'

const SHARING_OPTIONS = [
  {
    name: 'Personal',
    value: 'private',
    description: 'Only you can view and use this Workspace.',
    icon: <LockIcon className="h-[18px] w-[18px]" />,
  },
  {
    name: 'Organisation',
    value: 'organisation',
    description: 'Everyone in your organisation can use this Workspace.',
    icon: <UsersIcon className="h-[18px] w-[18px]" />,
  },
]

export const WorkspaceOverviewStep = () => {
  const [workspaceForm, setWorkspaceForm] = useAtom(riskWorkspaceAtom)
  const setWorkspaceSteps = useSetAtom(riskWorkspaceStepAtom)

  const { control, register, formState } = useForm<WorkspaceOverviewFormData>({
    resolver: zodResolver(WorkspaceOverviewSchema),
    mode: 'onChange',
    values: {
      name: workspaceForm.name,
      description: workspaceForm.description,
      visibility: workspaceForm.access,
    },
  })

  const nameField = register('name')
  const descriptionField = register('description')

  const wordCount = (text: string) => {
    if (!text?.trim()) return 0
    return text.trim().split(/\s+/).length
  }

  useEffect(() => {
    setWorkspaceSteps((prev) => ({ ...prev, overview: formState.isValid }))
  }, [formState.isValid, setWorkspaceSteps])

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-rexBlue-900 text-h3 font-semibold mb-1">Overview</h3>
        <p>Type details and choose who can use your workspace</p>
      </div>

      <form>
        <Label className="flex flex-col gap-1 mb-4 w-2/3">
          Name
          <Input
            {...nameField}
            onChange={(e) => {
              nameField.onChange(e)
              setWorkspaceForm((prev) => ({ ...prev, name: e.target.value }))
            }}
            placeholder="Type name - e.g. Three Waters Assessment"
            className="bg-white border border-neutral-200"
          />
          <p
            className={`${workspaceForm.name.length > 60 && 'text-red-500'} self-end text-xs text-neutral-600`}
          >
            {workspaceForm.name.length}/60 characters
          </p>
        </Label>

        <Label className="flex flex-col gap-1">
          Description
          <Textarea
            {...descriptionField}
            onChange={(e) => {
              descriptionField.onChange(e)
              setWorkspaceForm((prev) => ({ ...prev, description: e.target.value }))
            }}
            placeholder="Type description"
            rows={8}
            className="bg-white border border-neutral-200"
          />
          <p
            className={`${wordCount(workspaceForm.description) > 400 && 'text-red-500'} self-end text-xs text-neutral-600`}
          >
            {wordCount(workspaceForm.description)}/400 words
          </p>
        </Label>

        <Label className="flex flex-col gap-1">
          Sharing
          <div className="flex gap-4">
            {SHARING_OPTIONS.map((option, index) => (
              <Controller
                key={option.value + index}
                control={control}
                name="visibility"
                render={({ field: { value, ref, name } }) => (
                  <label
                    htmlFor={option.value}
                    className={`max-w-[250px] border-2 rounded-md bg-white p-4 flex flex-col gap-1 cursor-pointer ${value === option.value && 'border-primary !bg-rexBlue-50'}`}
                  >
                    <div className="flex justify-between mb-1">
                      {option.icon}
                      <RadioGroup
                        className="flex pl-2"
                        value={value}
                        onValueChange={(value: 'private' | 'organisation') =>
                          setWorkspaceForm((prev) => ({
                            ...prev,
                            access: value,
                          }))
                        }
                        name={name}
                      >
                        <RadioGroupItem
                          ref={ref}
                          value={option.value}
                          id={option.value}
                          className={`w-4 h-4 rounded-full border-2 border-gray-300 [&_svg]:h-2.5 [&_svg]:w-2.5 ${
                            value === option.value && 'border-primary'
                          }`}
                        />
                      </RadioGroup>
                    </div>
                    <p className="font-semibold">{option.name}</p>
                    <p className="text-xs text-neutral-600">{option.description}</p>
                  </label>
                )}
              />
            ))}
          </div>
        </Label>
      </form>
    </div>
  )
}
